// src/components/dashboard/NarrativeReportView.tsx
import React from 'react';
import { ChevronLeft, FileText, Calendar, Activity, Info, AlertTriangle } from 'lucide-react';

interface HealthMetric {
    id: string;
    metric_name: string;
    display_name: string;
    value: any; // Can be string or number
    numeric_value: number;
    unit: string;
    original_value?: string;
    status: string;
    reference_range_text: string;
    measured_at?: string;
    extracted_at: string;
}

interface NarrativeReportViewProps {
    title: string;
    date: string;
    metrics: HealthMetric[];
    onBack: () => void;
}

export const NarrativeReportView: React.FC<NarrativeReportViewProps> = ({
    title,
    date,
    metrics,
    onBack
}) => {
    const getStatusColor = (status: string) => {
        const s = status?.toLowerCase();
        if (s === 'normal' || s === 'negative') return 'var(--color-success)';
        if (s === 'high' || s === 'low' || s === 'borderline' || s === 'positive') return 'var(--color-warning)';
        if (s === 'critical') return 'var(--color-error)';
        return 'var(--color-secondary)';
    };

    return (
        <div style={{ padding: '0 var(--space-6) var(--space-8)', fontFamily: 'var(--font-primary)' }}>
            {/* Header */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-4)',
                marginBottom: 'var(--space-8)',
                paddingTop: 'var(--space-4)'
            }}>
                <button
                    onClick={onBack}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '40px',
                        height: '40px',
                        borderRadius: '12px',
                        border: '1px solid var(--color-border)',
                        backgroundColor: 'var(--color-surface)',
                        cursor: 'pointer',
                        color: 'var(--color-text)'
                    }}
                >
                    <ChevronLeft size={20} />
                </button>
                <div>
                    <h1 style={{ fontSize: 'var(--text-xl)', fontWeight: 700, margin: 0 }}>{title}</h1>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', color: 'var(--color-muted)', fontSize: 'var(--text-sm)' }}>
                        <Calendar size={14} />
                        <span>{date}</span>
                    </div>
                </div>
            </div>

            <div className="narrative-report-grid">
                {/* Findings Table */}
                <div>
                    <h2 style={{ fontSize: 'var(--text-lg)', fontWeight: 600, marginBottom: 'var(--space-4)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <FileText size={18} /> Detailed Findings
                    </h2>
                    <div className="table-scroll-wrapper" style={{
                        backgroundColor: 'var(--color-surface)',
                        border: '1px solid var(--color-border)',
                        borderRadius: 'var(--radius-lg)',
                        overflow: 'hidden'
                    }}>
                        <table style={{ width: '100%', minWidth: '500px', borderCollapse: 'collapse', textAlign: 'left' }}>
                            <thead>
                                <tr style={{ backgroundColor: 'var(--color-background)', borderBottom: '1px solid var(--color-border)' }}>
                                    <th style={{ padding: 'var(--space-4)', fontSize: 'var(--text-xs)', fontWeight: 700, color: 'var(--color-muted)', textTransform: 'uppercase' }}>Test Component</th>
                                    <th style={{ padding: 'var(--space-4)', fontSize: 'var(--text-xs)', fontWeight: 700, color: 'var(--color-muted)', textTransform: 'uppercase' }}>Result</th>
                                    <th style={{ padding: 'var(--space-4)', fontSize: 'var(--text-xs)', fontWeight: 700, color: 'var(--color-muted)', textTransform: 'uppercase' }}>Reference</th>
                                </tr>
                            </thead>
                            <tbody>
                                {metrics.map((metric, idx) => (
                                    <tr key={metric.id} style={{ borderBottom: idx === metrics.length - 1 ? 'none' : '1px solid var(--color-border)' }}>
                                        <td style={{ padding: 'var(--space-4)', fontWeight: 500 }}>{metric.display_name}</td>
                                        <td style={{ padding: 'var(--space-4)' }}>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                                    <span style={{ fontWeight: 600, wordBreak: 'break-word', whiteSpace: 'pre-line' }}>
                                                        {metric.value} {metric.unit}
                                                    </span>
                                                    {metric.status && metric.status.toLowerCase() !== 'normal' && (
                                                        <span style={{
                                                            fontSize: '10px',
                                                            padding: '2px 6px',
                                                            borderRadius: '4px',
                                                            backgroundColor: getStatusColor(metric.status).replace(')', ', 0.1)'),
                                                            color: getStatusColor(metric.status),
                                                            fontWeight: 700
                                                        }}>
                                                            {metric.status.toUpperCase()}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </td>
                                        <td style={{ padding: 'var(--space-4)', color: 'var(--color-muted)', fontSize: 'var(--text-sm)' }}>
                                            {metric.reference_range_text || 'N/A'}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* AI Insight Sidebar (Level 3 specific) */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)' }}>
                    <div style={{
                        backgroundColor: 'rgba(99, 102, 241, 0.05)',
                        border: '1px solid rgba(99, 102, 241, 0.1)',
                        borderRadius: 'var(--radius-lg)',
                        padding: 'var(--space-6)'
                    }}>
                        <h3 style={{ fontSize: 'var(--text-base)', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--color-primary)', marginBottom: 'var(--space-3)' }}>
                            <Activity size={18} /> Quick Summary
                        </h3>
                        <p style={{ fontSize: 'var(--text-sm)', lineHeight: 1.6, color: 'var(--color-text)', margin: 0 }}>
                            Based on this report from {date}, the results are overall {metrics.every(m => m.status.toLowerCase() === 'normal') ? 'normal' : 'stable with some notable variations'}.
                            Refer to the detailed findings for specific component values.
                        </p>
                    </div>

                    <div style={{
                        backgroundColor: 'var(--color-surface)',
                        border: '1px solid var(--color-border)',
                        borderRadius: 'var(--radius-lg)',
                        padding: 'var(--space-6)'
                    }}>
                        <h3 style={{ fontSize: 'var(--text-sm)', fontWeight: 700, color: 'var(--color-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 'var(--space-4)' }}>
                            Clinical Context
                        </h3>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
                            <div style={{ display: 'flex', gap: '12px' }}>
                                <Info size={16} style={{ color: 'var(--color-primary)', flexShrink: 0, marginTop: '2px' }} />
                                <p style={{ fontSize: 'var(--text-sm)', margin: 0 }}>Findings in this report should be correlated with clinical symptoms.</p>
                            </div>
                            {metrics.some(m => m.status.toLowerCase() === 'critical') && (
                                <div style={{ display: 'flex', gap: '12px', padding: '12px', backgroundColor: 'rgba(239, 68, 68, 0.05)', borderRadius: 'var(--radius)' }}>
                                    <AlertTriangle size={16} style={{ color: 'var(--color-error)', flexShrink: 0, marginTop: '2px' }} />
                                    <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-error)', fontWeight: 600, margin: 0 }}>Immediate follow-up recommended for critical findings.</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Responsive styles */}
            <style>{`
                .narrative-report-grid {
                    display: grid;
                    grid-template-columns: minmax(0, 2fr) minmax(0, 1fr);
                    gap: var(--space-8);
                }

                .table-scroll-wrapper {
                    overflow-x: auto;
                    -webkit-overflow-scrolling: touch;
                }

                @media (max-width: 768px) {
                    .narrative-report-grid {
                        grid-template-columns: 1fr;
                        gap: var(--space-4);
                    }

                    .table-scroll-wrapper table {
                        min-width: 450px;
                    }
                }
            `}</style>
        </div>
    );
};
